import type { ReactNode } from 'react';
import { ForbiddenPage } from '@/pages/forbidden-page';
import { useAuth } from './auth-context';

// Defence in depth for direct URL entry. This is NOT authorization — the API
// enforces the same permission independently on every request.
export function RequirePermission({
  permission,
  anyOf,
  derivable = false,
  staffPermission,
  organizationScoped = false,
  children,
}: {
  permission?: string;
  /** Admitted by any one of these instead — a screen every finance reader shares, the guide. */
  anyOf?: readonly string[];
  /**
   * A campaign staff assignment may derive the permission — one of the
   * fulfilment screens the server opens to it (docs/decisions/0090).
   */
  derivable?: boolean;
  /**
   * A campaign staff assignment reaches the screen through this permission
   * rather than `permission` — *Gelen Medya* (docs/decisions/0093).
   */
  staffPermission?: string;
  /** The screen reads an organization's money, which a `self` scope never holds. */
  organizationScoped?: boolean;
  children: ReactNode;
}) {
  const { can, canWork, staffMay, readsOrganizations } = useAuth();
  const admitted =
    anyOf !== undefined
      ? anyOf.some(can)
      : !permission ||
        (derivable ? canWork(permission) : can(permission)) ||
        (staffPermission !== undefined && staffMay(staffPermission));
  if (!admitted) return <ForbiddenPage />;
  if (organizationScoped && !readsOrganizations) return <ForbiddenPage />;
  return <>{children}</>;
}
